/* ══════════
   QVA Cart Select v1.3 — Shopee-style "select items to checkout".
   Companion of snippets/qva-cart-select.liquid + qva-cart-select-all.liquid.

   Design rules (do not break):
   1. SERVER IS TRUTH FOR DATA. Selection lives in line property "_qva_off"
      ("1" = off, absent = on). Every write still round-trips /cart/change.js
      with a fresh read-merge, and bars/totals are repainted from server
      Liquid once the queue drains — the server can never be out-argued.
   2. v1.2 OPTIMISTIC VISUALS. Measured 31 Aug 2026: a confirmed toggle costs
      ~1.4s (≈410ms /cart.js read + ≈1000ms /cart/change with sections; the
      change POST alone has a ≈800ms floor, so no trimming gets under ~1s).
      Therefore the checkbox, row dim, select-all counts and the "Tổng cộng"
      amount now update INSTANTLY client-side; the server sync runs in a
      background queue (last click per line wins) and the Liquid bars catch
      up on the drain repaint. Any sync error = full server repaint + error
      message, exactly like v1.1's revert.
   3. FAIL CLOSED AT CHECKOUT. Checkout first FLUSHES the sync queue, then
      re-reads the server cart. Unchecked lines are removed (stashed to
      localStorage first) and checkout proceeds ONLY after the removal is
      verified. Any failure = rollback + error message, never a wrong order.
   4. NATIVE SUBMIT AFTER REMOVAL. The original form is re-submitted with its
      checkout button (requestSubmit), so VAT attributes and the cart note
      still post to /cart exactly like stock Dawn. A bypass flag prevents
      re-interception.
   5. PROPERTIES MERGE. /cart/change.js REPLACES properties wholesale, so we
      always read the line's current properties first and merge.
   6. No alert()/confirm() — errors go to the cart error regions.
   7. v1.3 NO MARKER ON THE ORDER. Re-selecting a line has to write "_qva_off"
      = "0", because the cart API cannot delete a line property. That "0" is
      invisible to customers but it reached the ORDER in admin (seen on
      #3666 and #3678) and it splits the cart line when the same product is
      added again. Checkout now re-creates those lines clean before it
       submits — journal first, add clean, remove marked, and verify exact line
       identities and quantities. A confirmed no-change rejection may keep the
       cosmetic marker; an ambiguous mutation blocks checkout until recovery.
   ══════════ */
(() => {
  'use strict';
  if (window.QvaCartSelect) return;
  /* null means cart truth has not been read yet. Checkout must take the
     guarded path until we know whether a stale selection marker exists. */
  const S = (window.QvaCartSelect = { busy: false, hasMarkers: null });
  const activeCartMutationTokens = new Set();
  const activeExternalCartMutationTokens = new Set();
  let anonymousExternalMutationDepth = 0;
  let cartMutationSequence = 0;
  let restoreDeferred = false;
  let restoreBusy = false;

  function mutationToken(event) {
    return event && event.detail && event.detail.token ? String(event.detail.token) : '';
  }

  function mutationSource(event) {
    return event && event.detail && event.detail.source ? String(event.detail.source) : '';
  }

  function walletMutationIsBusy() {
    const root = document.documentElement;
    return !!(root && root.getAttribute('data-qva-wallet-mutation-busy') === 'true');
  }

  function externalCartMutationIsBusy() {
    const root = document.documentElement;
    return !!(
      walletMutationIsBusy() ||
      restoreBusy ||
      activeExternalCartMutationTokens.size ||
      anonymousExternalMutationDepth ||
      (activeCartMutationTokens.size === 0 && root && root.getAttribute('data-qva-cart-mutation-busy') === 'true')
    );
  }

  function syncCartMutationBusySignal() {
    const root = document.documentElement;
    if (!root) return;
    if (activeCartMutationTokens.size || activeExternalCartMutationTokens.size || anonymousExternalMutationDepth) {
      root.setAttribute('data-qva-cart-mutation-busy', 'true');
    } else {
      root.removeAttribute('data-qva-cart-mutation-busy');
    }
  }

  function beginCartMutation(source) {
    const token = `cart-select:${Date.now()}:${++cartMutationSequence}`;
    activeCartMutationTokens.add(token);
    syncCartMutationBusySignal();
    try {
      document.dispatchEvent(new CustomEvent('qva:cart-mutation-start', { detail: { token, source } }));
    } catch (_) { /* selection must keep working without the optional gateway */ }
    return token;
  }

  function finishCartMutation(token, source) {
    if (!token || !activeCartMutationTokens.delete(token)) return;
    syncCartMutationBusySignal();
    try {
      document.dispatchEvent(new CustomEvent('qva:cart-mutation-end', { detail: { token, source } }));
    } catch (_) { /* selection must keep working without the optional gateway */ }
  }

  const OFF = '_qva_off';
  const STASH_KEY = 'qva_saved_lines_v1';
  const MARKER_TXN_KEY = 'qva_marker_cleanup_v1';
  const CART_LOCK_NAME = 'qva-cart-transaction-v1';
  const GATEWAY_CONTINUATION_ATTR = 'data-qva-gateway-continuation';
  const GATEWAY_CONTINUATION_CLAIM = 'qva:account-gateway:continuation-claim';
  const GATEWAY_CONTINUATION_READY = 'qva:account-gateway:continuation-ready';
  const GATEWAY_CONTINUATION_CANCEL = 'qva:account-gateway:continuation-cancel';
  const STASH_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000; /* 14 days */
  const STASH_MAX_RESTORE_TRIES = 3;

  const R = window.routes || {};
  const CART_URL = R.cart_url || '/cart';
  const CHANGE_URL = R.cart_change_url || '/cart/change';
  const ADD_URL = R.cart_add_url || '/cart/add';

  function dispatchGatewayContinuation(name, form, token) {
    const detail = {
      source: 'qva-cart-select',
      form,
      token: String(token || ''),
      accepted: false,
    };
    try {
      document.dispatchEvent(new CustomEvent(name, { detail }));
    } catch (_) {
      return false;
    }
    return detail.accepted === true;
  }

  function claimGatewayContinuation(form) {
    const token = form?.getAttribute?.(GATEWAY_CONTINUATION_ATTR) || '';
    if (!token) return '';
    if (dispatchGatewayContinuation(GATEWAY_CONTINUATION_CLAIM, form, token)) return token;
    cancelGatewayContinuation(form, token);
    return '';
  }

  function readyGatewayContinuation(form, token) {
    if (!token) return true;
    return (
      dispatchGatewayContinuation(GATEWAY_CONTINUATION_READY, form, token) &&
      form.getAttribute(GATEWAY_CONTINUATION_ATTR) === token
    );
  }

  function cancelGatewayContinuation(form, token) {
    if (!token) return;
    dispatchGatewayContinuation(GATEWAY_CONTINUATION_CANCEL, form, token);
    if (form?.getAttribute?.(GATEWAY_CONTINUATION_ATTR) === token) {
      form.removeAttribute(GATEWAY_CONTINUATION_ATTR);
    }
  }

  const jsonHeaders = () =>
    typeof window.fetchConfig === 'function'
      ? window.fetchConfig()
      : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };

  /* v1.2: last cart JSON we saw (any read or write response). Used ONLY for
     the optimistic total math — every server write still does its own fresh
     read-merge, so a stale snapshot can never corrupt data. */
  let snapshot = null;

  const isTopLevelOff = (item) => item.properties && item.properties[OFF] === '1';
  /* v1.3: "1" means unchecked, but ANY value (including "0") is a leftover
     marker that must not travel to the order. */
  const hasMarker = (item) =>
    !!(item.properties && Object.prototype.hasOwnProperty.call(item.properties, OFF));

  function validateCart(c) {
    if (!c || typeof c !== 'object' || Array.isArray(c) || !Array.isArray(c.items)) {
      throw new Error('invalid cart response');
    }
    if (!Number.isInteger(Number(c.item_count)) || Number(c.item_count) < 0) {
      throw new Error('invalid cart item count');
    }
    let counted = 0;
    c.items.forEach((item) => {
      const id = item && (item.variant_id || item.id);
      const quantity = item && Number(item.quantity);
      if (
        !item ||
        typeof item !== 'object' ||
        typeof item.key !== 'string' ||
        !item.key ||
        (typeof id !== 'string' && typeof id !== 'number') ||
        String(id) === '' ||
        !Number.isSafeInteger(quantity) ||
        quantity <= 0 ||
        (item.properties !== null &&
          item.properties !== undefined &&
          (typeof item.properties !== 'object' || Array.isArray(item.properties)))
      ) {
        throw new Error('invalid cart line');
      }
      counted += quantity;
    });
    if (counted !== Number(c.item_count)) throw new Error('cart item count mismatch');
    return c;
  }

  function setSnapshot(c) {
    validateCart(c);
    snapshot = c;
    S.hasMarkers = c.items.some(hasMarker);
    return c;
  }

  const getCart = () =>
    // A cached post-add response can contain temporary replacement duplicates.
    // Every verification, including after Back, must read current server state.
    fetch(CART_URL + '.js', { cache: 'no-store', headers: { Accept: 'application/json' } }).then((r) => {
      if (!r.ok) throw new Error('cart.js HTTP ' + r.status);
      return r.json().then((c) => setSnapshot(c));
    });

  const errText = () =>
    (window.cartStrings && window.cartStrings.error) || 'Có lỗi xảy ra. Vui lòng thử lại.';

  function showError(msg, ctxEl) {
    const drawer = !!(ctxEl && ctxEl.closest && ctxEl.closest('cart-drawer'));
    const holder =
      document.getElementById(drawer ? 'CartDrawer-CartErrors' : 'cart-errors') ||
      document.getElementById('CartDrawer-CartErrors') ||
      document.getElementById('cart-errors');
    if (holder) holder.textContent = msg;
    else console.error('[QvaCartSelect]', msg);
  }
  function clearError() {
    ['CartDrawer-CartErrors', 'cart-errors'].forEach((id) => {
      const el = document.getElementById(id);
      if (el) el.textContent = '';
    });
  }

  /* ── section painting (mirrors Dawn's CartItems/CartDrawerItems) ──── */
  function presentSections() {
    const list = [];
    const items = document.getElementById('main-cart-items');
    const footer = document.getElementById('main-cart-footer');
    if (items && items.dataset.id) list.push({ id: 'main-cart-items', section: items.dataset.id, selector: '.js-contents' });
    if (footer && footer.dataset.id) list.push({ id: 'main-cart-footer', section: footer.dataset.id, selector: '.js-contents' });
    if (document.getElementById('CartDrawer')) list.push({ id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' });
    if (document.getElementById('cart-icon-bubble')) list.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });
    return list;
  }
  function paint(sectionsHtml, list) {
    if (!sectionsHtml) return;
    list.forEach((s) => {
      const html = sectionsHtml[s.section];
      const root = document.getElementById(s.id);
      if (!html || !root) return;
      const target = root.querySelector(s.selector) || root;
      const source = new DOMParser().parseFromString(html, 'text/html').querySelector(s.selector);
      if (source) target.innerHTML = source.innerHTML;
    });
  }
  async function repaintAll() {
    const list = presentSections();
    if (!list.length) return;
    const ids = [...new Set(list.map((s) => s.section))].join(',');
    const resp = await fetch(`${CART_URL}?sections=${encodeURIComponent(ids)}&sections_url=${encodeURIComponent(window.location.pathname)}`, { cache: 'no-store' });
    if (!resp.ok) throw new Error('sections HTTP ' + resp.status);
    paint(await resp.json(), list);
  }

  function lock(on) {
    document.documentElement.classList.toggle('qva-select-busy', on);
  }
  /* v1.2: while the background sync runs, the Liquid bars (tier / free-ship)
     briefly lag the optimistic total — signal it with a soft dim. */
  function setSyncing(on) {
    document.documentElement.classList.toggle('qva-syncing', on);
  }
  function refocus(key) {
    if (!key) return;
    try {
      const el = document.querySelector(`.qva-sel[data-key="${CSS.escape(key)}"] input`);
      if (el) el.focus({ preventScroll: true });
    } catch (_) { /* no-op */ }
  }

  /* ── derived UI state (buttons, indeterminate, express-pay) ──── */
  function syncFromDom() {
    document.querySelectorAll('.qva-sel-all').forEach((bar) => {
      const sel = Number(bar.dataset.selected || 0);
      const tot = Number(bar.dataset.total || 0);
      const input = bar.querySelector('input');
      if (input) {
        input.checked = tot > 0 && sel === tot;
        input.indeterminate = sel > 0 && sel < tot;
      }
    });
    const boxes = document.querySelectorAll('.qva-sel');
    if (!boxes.length) return;
    const selCount = document.querySelectorAll('.qva-sel[data-off="0"]').length;
    const anyOff = selCount < boxes.length;
    const markerTruthUnknownOrDirty = S.hasMarkers !== false;
    ['CartDrawer-Checkout', 'checkout'].forEach((id) => {
      const btn = document.getElementById(id);
      if (btn && !btn.hasAttribute('data-qva-native-disabled')) btn.disabled = selCount === 0;
    });
    document
      .querySelectorAll('.cart__dynamic-checkout-buttons')
      .forEach((el) => el.classList.toggle('qva-hidden', anyOff || markerTruthUnknownOrDirty));
  }

  /* ── v1.2 optimistic layer ────
     Flip everything the eye checks IMMEDIATELY; the queue + drain repaint
     below make the server agree a moment later. */

  /* KEY ROTATION (verified 1 Sep 2026): /cart/change.js re-hashes the line
     key on EVERY property write — even writing a previous value makes a NEW
     key. So a queued click or a post-repaint re-apply may hold a stale key.
     Resolve by exact key first, else by the variant prefix (the part before
     ":") when it matches exactly one line; ambiguous/gone -> let the drain
     repaint show server truth. */
  function findLine(items, key) {
    const exact = items.find((i) => i.key === key);
    if (exact) return exact;
    const pre = String(key).split(':')[0] + ':';
    const m = items.filter((i) => String(i.key).indexOf(pre) === 0);
    return m.length === 1 ? m[0] : null;
  }
  function findBoxes(key) {
    let list = [];
    try {
      list = [...document.querySelectorAll(`.qva-sel[data-key="${CSS.escape(key)}"]`)];
    } catch (_) { /* no-op */ }
    if (list.length) return list;
    const pre = String(key).split(':')[0] + ':';
    const all = [...document.querySelectorAll('.qva-sel')].filter(
      (b) => String(b.dataset.key || '').indexOf(pre) === 0
    );
    const uniq = new Set(all.map((b) => b.dataset.key));
    return uniq.size === 1 ? all : [];
  }

  /* "349.470" — VND has no decimals; Liquid money uses dot thousands. */
  function fmtMoney(subunits) {
    const units = Math.round(subunits / 100);
    return String(units).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  /* Mirror of the Liquid in main-cart-footer/cart-drawer: selected subtotal
     minus cart-level discounts, floored at 0. Nested (bundle) lines can't be
     told apart in cart.js, so they count by their own flag — the drain
     repaint corrects any bundle edge case within ~1s. */
  function optimisticTotals() {
    const c = snapshot;
    if (!c || !Array.isArray(c.items)) return;
    /* DOM state decides which lines count — matched by exact key, falling
       back to variant prefix (key rotation makes DOM and snapshot disagree
       mid-flight). A prefix with BOTH on and off boxes stays exact-only. */
    const offExact = new Set();
    const offPre = new Set();
    const onPre = new Set();
    document.querySelectorAll('.qva-sel').forEach((b) => {
      const k = b.dataset.key;
      if (!k) return;
      const p = String(k).split(':')[0];
      if (b.dataset.off === '1') {
        offExact.add(k);
        offPre.add(p);
      } else {
        onPre.add(p);
      }
    });
    const lineOff = (i) => {
      if (offExact.has(i.key)) return true;
      const p = String(i.key).split(':')[0];
      return offPre.has(p) && !onPre.has(p);
    };
    let sel = 0;
    c.items.forEach((i) => {
      if (!lineOff(i)) sel += i.final_line_price;
    });
    const disc = (c.items_subtotal_price || 0) - (c.total_price || 0);
    let est = sel - disc;
    if (est < 0) est = 0;
    document.querySelectorAll('.totals__total-value').forEach((el) => {
      const m = el.textContent.match(/[\d.,]+/);
      if (m) el.textContent = el.textContent.replace(m[0], fmtMoney(est));
    });
  }

  function applyOptimistic(key, wantSelected) {
    findBoxes(key).forEach((box) => {
      box.dataset.off = wantSelected ? '0' : '1';
      const input = box.querySelector('input');
      if (input) input.checked = wantSelected;
      const row = box.closest('tr') || box.closest('.cart-item');
      if (row) row.classList.toggle('qva-line--off', !wantSelected);
    });
    /* select-all bars: recount from the DOM (page + drawer render the same lines) */
    const keys = new Set();
    const selKeys = new Set();
    document.querySelectorAll('.qva-sel').forEach((b) => {
      if (!b.dataset.key) return;
      keys.add(b.dataset.key);
      if (b.dataset.off === '0') selKeys.add(b.dataset.key);
    });
    document.querySelectorAll('.qva-sel-all').forEach((bar) => {
      bar.dataset.selected = String(selKeys.size);
      bar.dataset.total = String(keys.size);
      const cnt = bar.querySelector('.qva-sel-all__count');
      if (cnt) cnt.textContent = `(${selKeys.size}/${keys.size} sản phẩm)`;
    });
    syncFromDom();
    optimisticTotals();
  }

  /* ── background sync queue (last click per line wins) ──── */
  const desired = new Map(); /* line key -> wantSelected */
  const settleWaiters = [];
  function syncSettled() {
    if (!S.busy && !restoreBusy && !desired.size) return Promise.resolve();
    return new Promise((r) => settleWaiters.push(r));
  }
  function scheduleSync() {
    if (S.busy || externalCartMutationIsBusy()) return; /* current owner will release/replay the queue */
    syncLoop();
  }
  async function syncLoop() {
    if (externalCartMutationIsBusy()) return;
    S.busy = true;
    setSyncing(true);
    clearError();
    let lastKey = null;
    const cartMutationToken = beginCartMutation('cart-select-sync');
    try {
      do {
        let dirty = false;
        while (desired.size) {
          const next = desired.entries().next().value;
          const key = next[0];
          const want = next[1];
          desired.delete(key);
          lastKey = key;
          const cart = await getCart(); /* fresh read every write — data safety */
          const item = findLine(cart.items, key); /* key may have rotated — see note above */
          if (!item) continue; /* line vanished/ambiguous — drain repaint shows truth */
          if (isTopLevelOff(item) === !want) continue; /* server already matches — skip */
          const props = Object.assign({}, item.properties || {});
          /* The cart API cannot DELETE a line property — verified 3 Sep 2026:
             properties:{}, properties:null, {_qva_off:null} and {_qva_off:""}
             all leave the key on the line. So re-selecting writes "0" here and
             checkout re-creates the line clean (see stripOffMarkers). Every
             consumer (Liquid, Function, this file) tests only for the value "1". */
          props[OFF] = want ? '0' : '1';
          const resp = await fetch(CHANGE_URL, {
            ...jsonHeaders(),
            /* item.key, NOT the queued key — the queued one may be stale (rotation) */
            body: JSON.stringify({ id: item.key, quantity: item.quantity, properties: props }),
          });
          const state = await resp.json();
          if (!resp.ok || state.errors) throw new Error(state.errors || 'HTTP ' + resp.status);
          setSnapshot(state);
          dirty = true;
        }
        if (dirty) await repaintAll(); /* one server repaint per drain: bars catch up */
        /* clicks that landed during the repaint were visually overwritten by
           server HTML — re-apply them, then loop to write them out */
        desired.forEach((want, key) => applyOptimistic(key, want));
      } while (desired.size);
      syncFromDom();
      refocus(lastKey);
    } catch (err) {
      console.error('[QvaCartSelect] sync', err);
      desired.clear();
      try {
        await getCart();
        await repaintAll(); /* full server reconcile = the v1.1 revert, cart-wide */
        syncFromDom();
      } catch (_) { /* keep first error */ }
      showError(errText(), document.body);
    } finally {
      setSyncing(false);
      S.busy = false;
      finishCartMutation(cartMutationToken, 'cart-select-sync');
      if (!desired.size) settleWaiters.splice(0).forEach((r) => r());
      else scheduleSync(); /* a click raced the finally — keep draining */
    }
  }

  function resumeDeferredSelection() {
    if (S.busy || restoreBusy || externalCartMutationIsBusy()) return;
    if (restoreDeferred) {
      restoreDeferred = false;
      Promise.resolve(restoreStash()).finally(() => window.setTimeout(resumeDeferredSelection, 0));
      return;
    }
    desired.forEach((want, key) => applyOptimistic(key, want));
    if (desired.size) scheduleSync();
    else settleWaiters.splice(0).forEach((resolve) => resolve());
  }

  document.addEventListener('qva:cart-mutation-start', (event) => {
    if (mutationSource(event).indexOf('cart-select') === 0) return;
    // Wallet owns a separate protected signal, already checked above. Do not
    // echo it into the generic signal that Wallet uses for competing writes.
    if (mutationSource(event) === 'wallet') return;
    const token = mutationToken(event);
    if (token) activeExternalCartMutationTokens.add(token);
    else anonymousExternalMutationDepth += 1;
    syncCartMutationBusySignal();
  });

  document.addEventListener('qva:cart-mutation-end', (event) => {
    if (mutationSource(event).indexOf('cart-select') === 0) return;
    if (mutationSource(event) !== 'wallet') {
      const token = mutationToken(event);
      if (token) activeExternalCartMutationTokens.delete(token);
      else anonymousExternalMutationDepth = Math.max(0, anonymousExternalMutationDepth - 1);
      syncCartMutationBusySignal();
    }
    window.setTimeout(resumeDeferredSelection, 0);
  });

  /* ── v1.3: clear leftover markers before the order is created ────
     A line can only lose "_qva_off" by being re-created, so for each marked
     line we ADD a clean copy first and only then remove the marked one.
     Add-before-remove means a failure between the two calls can leave a
     DUPLICATE (never a missing item), and the duplicate is compensated on
     the spot by subtracting exactly the quantity we added — the clean copy
     merges into an existing clean line of the same variant, so subtracting
     is right and deleting would not be.

     KEY ROTATION (verified 3 Sep 2026, the hard way): ANY cart write can
     re-hash EVERY line key — the add itself rotated all of them and removing
     by the key we read a moment earlier returned HTTP 400. So every round
     re-reads the cart and finds its target by identity (variant + marker +
     same properties + same quantity), never by a key read before a write.

     Inventory note: the add briefly asks for double the quantity, so on a
     product that denies overselling it can return 422. That is fine — the
     line keeps its harmless marker and checkout continues. */
  const normalizedProps = (properties, omitMarker = false) => {
    const result = {};
    Object.keys(properties || {})
      .sort()
      .forEach((key) => {
        if (!omitMarker || key !== OFF) result[key] = properties[key];
      });
    return result;
  };
  const sameProps = (a, b) => JSON.stringify(normalizedProps(a)) === JSON.stringify(normalizedProps(b));
  const sellingPlanId = (item) => {
    if (item && item.selling_plan !== null && item.selling_plan !== undefined) return item.selling_plan;
    return item && item.selling_plan_allocation && item.selling_plan_allocation.selling_plan
      ? item.selling_plan_allocation.selling_plan.id
      : null;
  };
  const sameSellingPlan = (a, b) => String(sellingPlanId(a) || '') === String(sellingPlanId(b) || '');
  const sameLineIdentity = (a, b) =>
    (a.variant_id || a.id) === (b.variant_id || b.id) &&
    sameProps(a.properties, b.properties) &&
    sameSellingPlan(a, b);
  const fullIdentity = (item) =>
    JSON.stringify([
      String(item.variant_id || item.id),
      String(sellingPlanId(item) || ''),
      normalizedProps(item.properties),
    ]);
  const cleanIdentity = (item) =>
    JSON.stringify([
      String(item.variant_id || item.id),
      String(sellingPlanId(item) || ''),
      normalizedProps(item.properties, true),
    ]);
  const quantityMap = (items, identity) => {
    const quantities = new Map();
    (items || []).forEach((item) => {
      const key = identity(item);
      quantities.set(key, (quantities.get(key) || 0) + Number(item.quantity || 0));
    });
    return quantities;
  };
  const mapShape = (quantities) =>
    JSON.stringify([...quantities.entries()].sort((a, b) => a[0].localeCompare(b[0])));
  const quantityShape = (items, identity = fullIdentity) => mapShape(quantityMap(items, identity));
  const cartShape = (cart, identity) => {
    const quantities = quantityMap(cart.items, identity);
    return JSON.stringify({
      item_count: Number(cart.item_count || 0),
      items_subtotal_price: Number(cart.items_subtotal_price || 0),
      total_price: Number(cart.total_price || 0),
      quantities: [...quantities.entries()].sort((a, b) => a[0].localeCompare(b[0])),
    });
  };
  const cleanCartShape = (cart) => cartShape(cart, cleanIdentity);
  const exactCartShape = (cart) => cartShape(cart, fullIdentity);
  const linePayload = (item, quantity) => {
    const payload = { id: item.variant_id || item.id, quantity };
    const properties = normalizedProps(item.properties);
    if (Object.keys(properties).length) payload.properties = properties;
    const planId = sellingPlanId(item);
    if (planId !== null && planId !== undefined) payload.selling_plan = planId;
    return payload;
  };
  const missingLinePayloads = (wanted, present) => {
    const wantedQuantities = new Map();
    const exemplars = new Map();
    (wanted || []).forEach((item) => {
      const identity = fullIdentity(item);
      wantedQuantities.set(identity, (wantedQuantities.get(identity) || 0) + Number(item.quantity || 0));
      if (!exemplars.has(identity)) exemplars.set(identity, item);
    });
    const presentQuantities = quantityMap(present || [], fullIdentity);
    const missing = [];
    wantedQuantities.forEach((quantity, identity) => {
      const deficit = quantity - (presentQuantities.get(identity) || 0);
      if (deficit > 0) missing.push(linePayload(exemplars.get(identity), deficit));
    });
    return missing;
  };
  const wantedLineQuantitiesMatch = (wanted, present) => {
    const wantedQuantities = quantityMap(wanted || [], fullIdentity);
    const presentQuantities = quantityMap(present || [], fullIdentity);
    for (const [identity, quantity] of wantedQuantities.entries()) {
      if ((presentQuantities.get(identity) || 0) !== quantity) return false;
    }
    return true;
  };
  const clearStash = () => {
    try { localStorage.removeItem(STASH_KEY); } catch (_) { /* no-op */ }
  };
  const saveStash = (stash) => {
    try {
      localStorage.setItem(STASH_KEY, JSON.stringify(stash));
      return true;
    } catch (_) {
      return false;
    }
  };
  const crossTabLockManager = () =>
    typeof navigator !== 'undefined' && navigator.locks && typeof navigator.locks.request === 'function'
      ? navigator.locks
      : null;
  const fatalStripError = (message) => {
    const error = new Error(message);
    error.qvaFatal = true;
    return error;
  };

  const markerTxnCartMatches = (txn, cart, field, countField) =>
    quantityShape(cart.items) === txn[field] && Number(cart.item_count) === Number(txn[countField]);

  function clearMarkerTxn() {
    try {
      localStorage.removeItem(MARKER_TXN_KEY);
      return localStorage.getItem(MARKER_TXN_KEY) === null;
    } catch (_) {
      return false;
    }
  }

  function readMarkerTxn() {
    let raw = null;
    try { raw = localStorage.getItem(MARKER_TXN_KEY); }
    catch (_) { throw fatalStripError('marker recovery storage unavailable'); }
    if (!raw) return null;
    let txn = null;
    try { txn = JSON.parse(raw); }
    catch (_) { throw fatalStripError('marker recovery record invalid'); }
    if (
      !txn ||
      (txn.v !== 1 && txn.v !== 2) ||
      typeof txn.baseline !== 'string' ||
      typeof txn.after_add !== 'string' ||
      typeof txn.completed !== 'string' ||
      (txn.v === 1 && (typeof txn.clean_identity !== 'string' ||
        !Number.isSafeInteger(Number(txn.clean_before)) || Number(txn.clean_before) < 0)) ||
      (txn.v === 2 && (typeof txn.expected_shape !== 'string' || !Array.isArray(txn.clean_lines) ||
        !txn.clean_lines.length || txn.clean_lines.some((line) =>
          typeof line.identity !== 'string' || !Number.isSafeInteger(line.before) || line.before < 0 ||
          !Number.isSafeInteger(line.added) || line.added <= 0)))
    ) {
      throw fatalStripError('marker recovery record invalid');
    }
    if (txn.v === 2) {
      try {
        for (const [field, count] of [['baseline', 'baseline_count'], ['after_add', 'after_add_count'], ['completed', 'completed_count']]) {
          const entries = JSON.parse(txn[field]);
          if (!Array.isArray(entries) || entries.some((entry) => !Array.isArray(entry) || entry.length !== 2 ||
              typeof entry[0] !== 'string' || !Number.isSafeInteger(entry[1]) || entry[1] <= 0) ||
              new Set(entries.map((entry) => entry[0])).size !== entries.length ||
              !Number.isSafeInteger(txn[count]) || entries.reduce((n, entry) => n + entry[1], 0) !== txn[count]) throw new Error();
        }
        if (new Set(txn.clean_lines.map((line) => line.identity)).size !== txn.clean_lines.length) throw new Error();
        const before = new Map(JSON.parse(txn.baseline)), after = new Map(before);
        txn.clean_lines.forEach((line) => {
          if ((before.get(line.identity) || 0) !== line.before) throw new Error();
          after.set(line.identity, line.before + line.added);
        });
        if (mapShape(after) !== txn.after_add) throw new Error();
      } catch (_) { throw fatalStripError('marker batch recovery record invalid'); }
    }
    return txn;
  }

  function persistMarkerTxn(txn) {
    const raw = JSON.stringify(txn);
    try {
      localStorage.setItem(MARKER_TXN_KEY, raw);
      return localStorage.getItem(MARKER_TXN_KEY) === raw;
    } catch (_) {
      return false;
    }
  }

  function createMarkerTxn(cart, markedLine, cleanPayload) {
    const baseline = quantityMap(cart.items, fullIdentity);
    const afterAdd = new Map(baseline);
    const cleanIdentityValue = fullIdentity(cleanPayload);
    const markedIdentityValue = fullIdentity(markedLine);
    const quantity = Number(markedLine.quantity);
    afterAdd.set(cleanIdentityValue, (afterAdd.get(cleanIdentityValue) || 0) + quantity);
    const completed = new Map(afterAdd);
    const remainingMarked = (completed.get(markedIdentityValue) || 0) - quantity;
    if (remainingMarked > 0) completed.set(markedIdentityValue, remainingMarked);
    else completed.delete(markedIdentityValue);
    return {
      v: 1,
      t: Date.now(),
      cart_token: typeof cart.token === 'string' ? cart.token : '',
      baseline: mapShape(baseline),
      baseline_count: Number(cart.item_count),
      after_add: mapShape(afterAdd),
      after_add_count: Number(cart.item_count) + quantity,
      completed: mapShape(completed),
      completed_count: Number(cart.item_count),
      clean_identity: cleanIdentityValue,
      clean_before: baseline.get(cleanIdentityValue) || 0,
    };
  }

  async function recoverMarkerTransaction(cart) {
    const txn = readMarkerTxn();
    if (!txn) return cart;
    if (cart.item_count === 0 || (txn.cart_token && cart.token && txn.cart_token !== cart.token)) {
      if (!clearMarkerTxn()) throw fatalStripError('marker recovery record could not be cleared');
      return cart;
    }
    if (txn.v === 2) return recoverMarkerBatch(txn, cart);
    if (
      markerTxnCartMatches(txn, cart, 'baseline', 'baseline_count') ||
      markerTxnCartMatches(txn, cart, 'completed', 'completed_count')
    ) {
      if (!clearMarkerTxn()) throw fatalStripError('marker recovery record could not be cleared');
      return cart;
    }
    if (!markerTxnCartMatches(txn, cart, 'after_add', 'after_add_count')) {
      throw fatalStripError('marker recovery cart does not match a safe state');
    }

    const cleanLines = cart.items.filter((item) => fullIdentity(item) === txn.clean_identity);
    if (cleanLines.length !== 1) throw fatalStripError('marker recovery clean line is ambiguous');
    try {
      await fetch(CHANGE_URL, {
        ...jsonHeaders(),
        body: JSON.stringify({ id: cleanLines[0].key, quantity: Number(txn.clean_before) }),
      });
    } catch (_) {
      /* Absolute quantity makes this compensation safe to reconcile/retry. */
    }
    let reconciled = null;
    try { reconciled = await getCart(); }
    catch (_) { throw fatalStripError('marker recovery verification failed'); }
    if (!markerTxnCartMatches(txn, reconciled, 'baseline', 'baseline_count')) {
      throw fatalStripError('marker recovery compensation failed');
    }
    if (!clearMarkerTxn()) throw fatalStripError('marker recovery record could not be cleared');
    return reconciled;
  }

  async function updateExactLines(cart, quantities) {
    const updates = {};
    quantities.forEach((quantity, identity) => {
      const lines = cart.items.filter((item) => fullIdentity(item) === identity);
      if (lines.length !== 1 || typeof lines[0].key !== 'string' || !lines[0].key.includes(':') ||
          !Number.isSafeInteger(quantity) || quantity < 0 || quantity > lines[0].quantity) {
        throw fatalStripError('batch quantity target is ambiguous');
      }
      updates[lines[0].key] = quantity;
    });
    let response = null;
    try {
      response = await fetch(CART_URL + '/update.js', {
        ...jsonHeaders(), body: JSON.stringify({ updates }),
      });
    } catch (_) { /* Read server truth after a lost response; never repeat the write here. */ }
    if (response && response.ok) {
      try { return setSnapshot(await response.json()); } catch (_) { /* Verify with a fresh read. */ }
    }
    try { return await getCart(); }
    catch (_) { throw fatalStripError('batch quantity verification unavailable'); }
  }

  function createMarkerBatch(cart, marked, payloads) {
    const baseline = quantityMap(cart.items, fullIdentity);
    const added = quantityMap(payloads, fullIdentity);
    const afterAdd = new Map(baseline);
    added.forEach((quantity, identity) => afterAdd.set(identity, (afterAdd.get(identity) || 0) + quantity));
    const completed = new Map(afterAdd);
    marked.forEach((item) => completed.delete(fullIdentity(item)));
    return {
      v: 2, t: Date.now(), cart_token: typeof cart.token === 'string' ? cart.token : '',
      baseline: mapShape(baseline), baseline_count: Number(cart.item_count),
      after_add: mapShape(afterAdd), after_add_count: Number(cart.item_count) + marked.reduce((n, i) => n + i.quantity, 0),
      completed: mapShape(completed), completed_count: Number(cart.item_count),
      expected_shape: cleanCartShape(cart),
      clean_lines: [...added].map(([identity, quantity]) => ({ identity, before: baseline.get(identity) || 0, added: quantity })),
    };
  }

  async function recoverMarkerBatch(txn, cart) {
    const clearVerified = (state) => {
      if (!clearMarkerTxn()) throw fatalStripError('marker recovery record could not be cleared');
      return state;
    };
    if (markerTxnCartMatches(txn, cart, 'baseline', 'baseline_count') ||
        markerTxnCartMatches(txn, cart, 'completed', 'completed_count')) return clearVerified(cart);

    const before = new Map(JSON.parse(txn.baseline));
    const present = quantityMap(cart.items, fullIdentity);
    const clean = new Map(txn.clean_lines.map((line) => [line.identity, line]));
    const identities = new Set([...before.keys(), ...present.keys()]);
    // A partially accepted add can only increase the exact clean identities by
    // up to the journaled amount. Restore absolute baseline quantities once.
    const canUndoAdd = [...identities].every((identity) => {
      const now = present.get(identity) || 0, old = before.get(identity) || 0, line = clean.get(identity);
      return line ? old === line.before && now >= old && now <= old + line.added : now === old;
    });
    if (canUndoAdd) {
      const changes = new Map(txn.clean_lines.filter((line) => (present.get(line.identity) || 0) > line.before)
        .map((line) => [line.identity, line.before]));
      const restored = await updateExactLines(cart, changes);
      if (!markerTxnCartMatches(txn, restored, 'baseline', 'baseline_count')) {
        throw fatalStripError('batch add compensation failed');
      }
      return clearVerified(restored);
    }

    // A partial removal is recoverable only when every clean replacement is
    // already exact and each original marked line is either intact or absent.
    const completed = new Map(JSON.parse(txn.completed));
    const afterAdd = new Map(JSON.parse(txn.after_add));
    const removable = new Set([...afterAdd.keys()].filter((identity) => !completed.has(identity)));
    const canFinishRemoval = [...new Set([...afterAdd.keys(), ...present.keys()])].every((identity) => {
      const now = present.get(identity) || 0;
      return removable.has(identity) ? now === 0 || now === afterAdd.get(identity) : now === (completed.get(identity) || 0);
    });
    if (!canFinishRemoval) throw fatalStripError('batch recovery cart does not match a safe state');
    const changes = new Map([...removable].filter((identity) => present.has(identity)).map((identity) => [identity, 0]));
    const finished = await updateExactLines(cart, changes);
    if (!markerTxnCartMatches(txn, finished, 'completed', 'completed_count')) {
      throw fatalStripError('batch removal recovery failed');
    }
    return clearVerified(finished);
  }

  async function stripMarkerBatch(cart) {
    const marked = cart.items.filter(hasMarker);
    const payloads = marked.map((item) => {
      const payload = linePayload(item, item.quantity);
      payload.properties = normalizedProps(item.properties, true);
      if (!Object.keys(payload.properties).length) delete payload.properties;
      return payload;
    });
    const txn = createMarkerBatch(cart, marked, payloads);
    if (!persistMarkerTxn(txn)) throw new Error('marker cleanup recovery storage unavailable');
    try {
      await fetch(ADD_URL + '.js', { ...jsonHeaders(), body: JSON.stringify({ items: payloads }) });
    } catch (_) { /* Server read distinguishes a committed add from a rejected one. */ }
    let fresh;
    try { fresh = await getCart(); } catch (_) { throw fatalStripError('batch post-add cart read failed'); }
    if (!markerTxnCartMatches(txn, fresh, 'after_add', 'after_add_count')) {
      const recovered = await recoverMarkerBatch(txn, fresh);
      if (cleanCartShape(recovered) !== txn.expected_shape) throw fatalStripError('batch recovery totals changed');
      if (markerTxnCartMatches(txn, recovered, 'baseline', 'baseline_count')) {
        throw new Error('batch add rejected; original cart preserved');
      }
      return recovered;
    }
    const changes = new Map(marked.map((item) => [fullIdentity(item), 0]));
    const after = await updateExactLines(fresh, changes);
    const verified = await recoverMarkerBatch(txn, after);
    if (cleanCartShape(verified) !== txn.expected_shape) throw fatalStripError('batch checkout totals changed');
    if (!markerTxnCartMatches(txn, verified, 'completed', 'completed_count')) {
      throw new Error('batch removal failed; original cart preserved');
    }
    return verified;
  }

  async function stripOffMarkers(cart) {
    cart = await recoverMarkerTransaction(cart);
    if (!cart.items.some(hasMarker)) return cart;
    // Split price/discount lines can share an identity. Keep their existing
    // single-line handling; the fast path requires one exact line per identity.
    if (cart.items.filter(hasMarker).length > 1 &&
        new Set(cart.items.map(fullIdentity)).size === cart.items.length) return stripMarkerBatch(cart);
    const expectedShape = cleanCartShape(cart);
    const maxRounds = cart.items.filter(hasMarker).length;
    let lastState = null; /* the cart JSON returned by the last successful removal */
    /* One round per marked line; the guard stops any pathological loop.
       Round 0 reuses the cart the caller already read, so a cart with no
       marker (the normal case) costs ZERO extra requests. */
    let current = cart;
    for (let guard = 0; guard < maxRounds; guard++) {
      if (guard > 0) current = lastState || (await getCart());
      const marked = current.items.filter(hasMarker);
      if (!marked.length) break;
      const it = marked[0];
      const vid = it.variant_id || it.id;
      const keep = {};
      Object.keys(it.properties || {}).forEach((k) => {
        if (k !== OFF) keep[k] = it.properties[k];
      });
      const payload = { id: vid, quantity: it.quantity };
      if (Object.keys(keep).length) payload.properties = keep;
      const planId = sellingPlanId(it);
      if (planId !== null && planId !== undefined) payload.selling_plan = planId;
      const markerTxn = createMarkerTxn(current, it, payload);
      if (!persistMarkerTxn(markerTxn)) {
        /* No cart write occurred. Skipping cosmetic cleanup is safe; performing
           an unjournaled add is not. */
        throw new Error('marker cleanup recovery storage unavailable');
      }
      let addFailure = '';
      try {
        const add = await fetch(ADD_URL + '.js', {
          ...jsonHeaders(),
          body: JSON.stringify({ items: [payload] }),
        });
        if (!add.ok) addFailure = 'strip add HTTP ' + add.status;
      } catch (err) {
        addFailure = (err && err.message) || 'strip add outcome unknown';
      }
      /* A rejected/lost response may arrive after Shopify committed the add.
         Resolve that ambiguity with cart truth before removing anything. */
      let fresh = null;
      try { fresh = await getCart(); } catch (_) { throw fatalStripError('strip post-add cart read failed'); }
      if (markerTxnCartMatches(markerTxn, fresh, 'baseline', 'baseline_count')) {
        if (!clearMarkerTxn()) throw fatalStripError('marker recovery record could not be cleared');
        throw new Error(addFailure || 'strip add made no cart change');
      }
      if (!markerTxnCartMatches(markerTxn, fresh, 'after_add', 'after_add_count')) {
        throw fatalStripError('strip add verification failed');
      }
      const target = fresh.items.find(
        (x) =>
          hasMarker(x) &&
          (x.variant_id || x.id) === vid &&
          x.quantity === it.quantity &&
          sameProps(x.properties, it.properties) &&
          sameSellingPlan(x, it)
      );
      let failure = '';
      if (!target) {
        failure = 'marked line not found after add';
      } else {
        let rm = null;
        let removeFailure = '';
        try {
          rm = await fetch(CHANGE_URL, {
            ...jsonHeaders(),
            body: JSON.stringify({ id: target.key, quantity: 0 }),
          });
        } catch (err) {
          removeFailure = (err && err.message) || 'strip remove outcome unknown';
        }
        if (rm && !rm.ok) removeFailure = 'strip remove HTTP ' + rm.status;
        if (removeFailure) failure = removeFailure;
        else {
          /* /cart/change.js answers with the whole new cart — reuse it as the
             next round's state instead of paying for another read. */
          try { lastState = setSnapshot(await rm.json()); }
          catch (_) {
            try { lastState = await getCart(); }
            catch (_) { throw fatalStripError('strip post-remove cart read failed'); }
          }
        }
      }
      if (failure) {
        /* Compensate by exact line identity, then prove the complete cart shape
           is identical to the state before the add. A failed or ambiguous
           compensation is financial, not cosmetic, so checkout must stop. */
        let safeState = '';
        try {
          const back = await getCart();
          if (markerTxnCartMatches(markerTxn, back, 'baseline', 'baseline_count')) {
            lastState = back;
            safeState = 'unchanged';
          } else if (markerTxnCartMatches(markerTxn, back, 'completed', 'completed_count')) {
            lastState = back;
            safeState = 'completed';
          }
          if (safeState) {
            /* The failed response either left the original cart untouched or
               actually completed the removal. Both are financially safe. */
          } else {
            const dupe = back.items.find(
              (x) =>
                (x.variant_id || x.id) === vid &&
                !hasMarker(x) &&
                sameProps(x.properties, keep) &&
                sameSellingPlan(x, it)
            );
            if (!dupe || dupe.quantity < it.quantity) throw new Error('clean compensation line not found');
            const undo = await fetch(CHANGE_URL, {
              ...jsonHeaders(),
              body: JSON.stringify({ id: dupe.key, quantity: dupe.quantity - it.quantity }),
            });
            if (!undo.ok) throw new Error('strip compensation HTTP ' + undo.status);
            let restored = null;
            try { restored = setSnapshot(await undo.json()); } catch (_) { restored = await getCart(); }
            if (!markerTxnCartMatches(markerTxn, restored, 'baseline', 'baseline_count')) {
              throw new Error('strip compensation verification failed');
            }
            lastState = restored;
            safeState = 'unchanged';
          }
        } catch (err2) {
          console.error('[QvaCartSelect] strip compensate', err2);
          throw fatalStripError(`${failure}; compensation failed`);
        }
        if (!clearMarkerTxn()) throw fatalStripError('marker recovery record could not be cleared');
        if (safeState === 'completed') continue;
        throw new Error(failure);
      }
      if (!markerTxnCartMatches(markerTxn, lastState, 'completed', 'completed_count')) {
        throw fatalStripError('strip removal verification failed');
      }
      if (!clearMarkerTxn()) throw fatalStripError('marker recovery record could not be cleared');
    }
    let after = lastState;
    if (!after) {
      try { after = await getCart(); }
      catch (_) { throw fatalStripError('strip final cart read failed'); }
    }
    if (after.items.some(hasMarker) || cleanCartShape(after) !== expectedShape) {
      /* Quantities, selling plans, non-marker properties and prices must all
         remain identical. Anything else is not what the customer approved. */
      throw fatalStripError('strip verification failed');
    }
    return after;
  }

  /* The marker is cosmetic, so a confirmed no-change rejection may keep it.
     Any ambiguous write or failed verification is financial and stops checkout. */
  async function stripMarkersSafely(cart) {
    try {
      return await stripOffMarkers(cart);
    } catch (err) {
      if (err && err.qvaFatal) throw err;
      console.warn('[QvaCartSelect] marker strip skipped:', (err && err.message) || err);
      return cart;
    }
  }

  async function restoreCheckoutBaseline(baseline, stashedItems) {
    const baselineShape = exactCartShape(baseline);
    let current = await getCart();
    if (exactCartShape(current) === baselineShape) return current;

    /* Only repair deficits belonging to the lines this checkout attempt
       removed. Any unrelated change may come from another tab and must never
       be overwritten by an automatic rollback. */
    const allowedDeficits = new Set((stashedItems || []).map(fullIdentity));
    const beforeQuantities = quantityMap(baseline.items, fullIdentity);
    const currentQuantities = quantityMap(current.items, fullIdentity);
    const identities = new Set([...beforeQuantities.keys(), ...currentQuantities.keys()]);
    identities.forEach((identity) => {
      const before = beforeQuantities.get(identity) || 0;
      const now = currentQuantities.get(identity) || 0;
      if ((allowedDeficits.has(identity) && now > before) || (!allowedDeficits.has(identity) && now !== before)) {
        throw new Error('rollback found an unrelated cart change');
      }
    });

    const missing = missingLinePayloads(stashedItems, current.items);
    if (!missing.length) throw new Error('rollback cart shape mismatch');

    let addError = null;
    try {
      const resp = await fetch(ADD_URL + '.js', { ...jsonHeaders(), body: JSON.stringify({ items: missing }) });
      if (!resp.ok) addError = new Error('rollback add HTTP ' + resp.status);
    } catch (err) {
      addError = err;
    }

    /* A rejected/lost response can still mean Shopify committed the add. The
       fresh read is the verdict; never retry this POST inside the attempt. */
    current = await getCart();
    if (exactCartShape(current) !== baselineShape) {
      throw addError || new Error('rollback verification failed');
    }
    return current;
  }

  /* ── checkout interception (fail closed) ──── */
  function submitPreparedCheckout(form, submitter, gatewayContinuationToken) {
    if (!readyGatewayContinuation(form, gatewayContinuationToken)) {
      throw new Error('gateway continuation was not accepted');
    }
    form.dataset.qvaBypass = '1';
    let submitted = false;
    /* The AJAX preparation above is the quantity authority. Cleaning markers
       or removing unchecked lines can reorder Shopify's cart while this form
       still has its original row order. Posting updates[] here would apply
       those old positional quantities to different products. Exclude only
       these quantity controls while native submission constructs its payload;
       note, VAT attributes, discounts and the checkout submitter stay intact. */
    const quantityControls = Array.from(form.elements || [])
      .filter((control) => control.name === 'updates[]')
      .map((control) => ({ control, disabled: control.disabled }));
    try {
      quantityControls.forEach(({ control }) => { control.disabled = true; });
      submitted = submitNative(form, submitter);
    } catch (error) {
      delete form.dataset.qvaBypass;
      throw error;
    } finally {
      quantityControls.forEach(({ control, disabled }) => { control.disabled = disabled; });
    }
    if (
      gatewayContinuationToken &&
      (!submitted || form.getAttribute(GATEWAY_CONTINUATION_ATTR) === gatewayContinuationToken)
    ) {
      delete form.dataset.qvaBypass;
      throw new Error('gateway continuation was not consumed');
    }
  }

  async function partialCheckoutLocked(form, submitter, crossTabSafe, gatewayContinuationToken = '') {
    if (S.busy) {
      cancelGatewayContinuation(form, gatewayContinuationToken);
      return;
    }
    if (externalCartMutationIsBusy()) {
      cancelGatewayContinuation(form, gatewayContinuationToken);
      showError('Giỏ hàng đang cập nhật. Vui lòng chờ hoàn tất rồi thanh toán.', form);
      return;
    }
    S.busy = true;
    lock(true);
    clearError();
    if (submitter) submitter.setAttribute('aria-busy', 'true');
    const cartMutationToken = beginCartMutation('cart-select-checkout');
    let stashPayload = null;
    let checkoutBaseline = null;
    let removalAttempted = false;
    let cartRecoveryVerified = false;
    let continuationSettled = !gatewayContinuationToken;
    try {
      const cart = await getCart();
      checkoutBaseline = cart;
      const offItems = cart.items.filter(isTopLevelOff);
      const selCount = cart.items.length - offItems.length;
      if (selCount <= 0) {
        showError('Vui lòng chọn ít nhất 1 sản phẩm để thanh toán.', form);
        return;
      }
      if (!crossTabSafe) {
        if (readMarkerTxn()) {
          showError('Vui lòng mở trang này bằng trình duyệt mới hơn rồi kiểm tra lại giỏ hàng.', form);
          return;
        }
        if (offItems.length) {
          showError('Trình duyệt này chưa hỗ trợ thanh toán một phần. Vui lòng chọn lại tất cả sản phẩm.', form);
          return;
        }
        /* No cross-tab lock means no temporary add/remove is allowed. Sending
           a harmless private marker is safer than risking a quantity race. */
        submitPreparedCheckout(form, submitter, gatewayContinuationToken);
        continuationSettled = true;
        return;
      }
      if (!offItems.length) {
        /* nothing to remove — but a re-selected line may still carry "0" */
        await stripMarkersSafely(cart);
        submitPreparedCheckout(form, submitter, gatewayContinuationToken);
        continuationSettled = true;
        return;
      }
      /* 1 — stash for restore (Shopee behavior) */
      stashPayload = {
        t: Date.now(),
        r: 0,
        items: offItems.map((i) => {
          const stashed = { id: i.variant_id || i.id, quantity: i.quantity, properties: i.properties || {} };
          const planId = sellingPlanId(i);
          if (planId !== null && planId !== undefined) stashed.selling_plan = planId;
          return stashed;
        }),
      };
      if (!saveStash(stashPayload)) {
        showError(
          'Trình duyệt đang chặn bộ nhớ tạm. Hãy chọn lại tất cả sản phẩm hoặc bật bộ nhớ trình duyệt trước khi thanh toán.',
          form
        );
        return;
      }
      /* 2 — remove unchecked lines. Every cart write can rotate every key,
         so resolve each next target from the preceding server response. */
      let removalState = cart;
      if (offItems.length > 1 && new Set(cart.items.map(fullIdentity)).size === cart.items.length) {
        removalAttempted = true;
        removalState = await updateExactLines(cart, new Map(offItems.map((item) => [fullIdentity(item), 0])));
      } else for (const it of offItems) {
        const target = removalState.items.find((candidate) => isTopLevelOff(candidate) && sameLineIdentity(candidate, it));
        if (!target) throw new Error('remove target missing after key rotation');
        removalAttempted = true;
        let resp = null;
        try {
          resp = await fetch(CHANGE_URL, {
            ...jsonHeaders(),
            body: JSON.stringify({ id: target.key, quantity: 0 }),
          });
        } catch (_) {
          throw fatalStripError('unchecked removal outcome unknown');
        }
        if (!resp.ok) throw new Error('remove HTTP ' + resp.status);
        try { removalState = setSnapshot(await resp.json()); }
        catch (_) {
          try { removalState = await getCart(); }
          catch (_) { throw fatalStripError('unchecked removal cart read failed'); }
        }
      }
      /* 3 — verify: no off-lines may remain */
      const after = removalState;
      if (after.items.some(isTopLevelOff)) throw new Error('verification failed: off-lines remain');
      if (after.item_count <= 0) throw new Error('verification failed: cart empty after removal');
      const wanted = cart.items.filter((item) => !isTopLevelOff(item));
      if (quantityShape(after.items) !== quantityShape(wanted)) throw new Error('verification failed: selected quantities changed');
      /* 3b — v1.3: re-create any line still carrying a "0" marker */
      await stripMarkersSafely(after);
      /* 4 — native submit (posts VAT attributes + note, then checkout) */
      submitPreparedCheckout(form, submitter, gatewayContinuationToken);
      continuationSettled = true;
    } catch (err) {
      console.error('[QvaCartSelect] checkout', err);
      /* Roll back to the exact pre-checkout cart. Durable stash is cleared only
         after a validated read proves recovery, including lost-response cases. */
      try {
        if (stashPayload && checkoutBaseline) {
          if (removalAttempted) await restoreCheckoutBaseline(checkoutBaseline, stashPayload.items);
          else {
            const unchanged = await getCart();
            if (exactCartShape(unchanged) !== exactCartShape(checkoutBaseline)) {
              throw new Error('pre-removal cart changed');
            }
          }
          clearStash();
          cartRecoveryVerified = true;
        } else if (checkoutBaseline) {
          const reconciled = await recoverMarkerTransaction(await getCart());
          cartRecoveryVerified = cleanCartShape(reconciled) === cleanCartShape(checkoutBaseline);
        }
        await repaintAll();
        syncFromDom();
        showError(
          cartRecoveryVerified
            ? 'Không thể xử lý thanh toán. Giỏ hàng đã được khôi phục — vui lòng thử lại.'
            : 'Có lỗi khi xử lý giỏ hàng. Vui lòng tải lại trang và kiểm tra giỏ trước khi thanh toán.',
          form
        );
      } catch (err2) {
        console.error('[QvaCartSelect] rollback', err2);
        showError('Có lỗi khi xử lý giỏ hàng. Vui lòng tải lại trang và kiểm tra giỏ trước khi thanh toán.', form);
      }
    } finally {
      if (!continuationSettled) cancelGatewayContinuation(form, gatewayContinuationToken);
      if (submitter) submitter.removeAttribute('aria-busy');
      lock(false);
      S.busy = false;
      finishCartMutation(cartMutationToken, 'cart-select-checkout');
      if (desired.size) scheduleSync(); /* toggles queued during checkout */
      else settleWaiters.splice(0).forEach((r) => r());
    }
  }

  let checkoutWaitingForLock = false;
  async function partialCheckout(form, submitter, gatewayContinuationToken = '') {
    if (S.busy || checkoutWaitingForLock) {
      cancelGatewayContinuation(form, gatewayContinuationToken);
      return;
    }
    checkoutWaitingForLock = true;
    const waitToken = beginCartMutation('cart-select-checkout-wait');
    try {
      const locks = crossTabLockManager();
      if (locks) {
        await locks.request(CART_LOCK_NAME, { mode: 'exclusive' }, () =>
          partialCheckoutLocked(form, submitter, true, gatewayContinuationToken)
        );
      } else {
        await partialCheckoutLocked(form, submitter, false, gatewayContinuationToken);
      }
    } catch (err) {
      cancelGatewayContinuation(form, gatewayContinuationToken);
      console.error('[QvaCartSelect] checkout lock', err);
      showError('Giỏ hàng đang được xử lý trong một tab khác. Vui lòng chờ rồi thử lại.', form);
    } finally {
      finishCartMutation(waitToken, 'cart-select-checkout-wait');
      checkoutWaitingForLock = false;
    }
  }

  function submitNative(form, submitter) {
    if (typeof form.requestSubmit === 'function') {
      let submitEvent = null;
      const canObserveSubmit =
        typeof form.addEventListener === 'function' && typeof form.removeEventListener === 'function';
      const rememberSubmit = (event) => {
        if (event.target === form) submitEvent = event;
      };
      if (canObserveSubmit) form.addEventListener('submit', rememberSubmit, true);
      try {
        if (submitter && submitter.form === form) form.requestSubmit(submitter);
        else form.requestSubmit();
      } finally {
        if (canObserveSubmit) form.removeEventListener('submit', rememberSubmit, true);
      }
      return canObserveSubmit ? Boolean(submitEvent && !submitEvent.defaultPrevented) : true;
    } else {
      form.submit(); /* ancient browsers: falls back to plain cart post */
      return true;
    }
  }

  /* ── restore stash (runs on every page load; drawer JS is site-wide) ── */
  async function restoreStash() {
    if (S.busy || restoreBusy || externalCartMutationIsBusy()) {
      restoreDeferred = true;
      return;
    }
    let raw = null;
    try {
      raw = localStorage.getItem(STASH_KEY);
    } catch (_) {
      return;
    }
    if (!raw) return;
    let stash = null;
    try { stash = JSON.parse(raw); }
    catch (_) {
      clearStash();
      return;
    }
    if (!stash || !Array.isArray(stash.items) || !stash.items.length) {
      clearStash();
      return;
    }
    if (Date.now() - (stash.t || 0) > STASH_MAX_AGE_MS) {
      clearStash();
      return;
    }
    if ((stash.r || 0) >= STASH_MAX_RESTORE_TRIES) {
      showError('Không thể tự khôi phục một số sản phẩm. Vui lòng kiểm tra lại giỏ hàng.', document.body);
      return;
    }
    const locks = crossTabLockManager();
    if (!locks) {
      showError('Trình duyệt này chưa hỗ trợ khôi phục giỏ hàng an toàn. Vui lòng kiểm tra lại giỏ hàng.', document.body);
      return;
    }
    restoreBusy = true;
    const cartMutationToken = beginCartMutation('cart-select-restore');
    let dataRestored = false;
    try {
      await locks.request(CART_LOCK_NAME, { mode: 'exclusive' }, async () => {
        /* Another tab may have consumed or updated the stash while this tab
           waited for the lock. Always re-read it inside the critical section. */
        let lockedRaw = null;
        try { lockedRaw = localStorage.getItem(STASH_KEY); } catch (_) { return; }
        if (!lockedRaw) return;
        if (lockedRaw !== raw) {
          try { stash = JSON.parse(lockedRaw); } catch (_) { return; }
          if (!stash || !Array.isArray(stash.items) || !stash.items.length) return;
          if (Date.now() - (stash.t || 0) > STASH_MAX_AGE_MS) return;
          if ((stash.r || 0) >= STASH_MAX_RESTORE_TRIES) return;
        }
        try {
          let current = await getCart();
          const missing = missingLinePayloads(stash.items, current.items);
          if (missing.length) {
            const expectedQuantities = quantityMap(current.items, fullIdentity);
            let expectedCount = Number(current.item_count);
            missing.forEach((item) => {
              const identity = fullIdentity(item);
              const quantity = Number(item.quantity);
              expectedQuantities.set(identity, (expectedQuantities.get(identity) || 0) + quantity);
              expectedCount += quantity;
            });
            const expectedShape = mapShape(expectedQuantities);
            let addError = null;
            try {
              const resp = await fetch(ADD_URL + '.js', { ...jsonHeaders(), body: JSON.stringify({ items: missing }) });
              if (!resp.ok) addError = new Error('restore HTTP ' + resp.status);
            } catch (err) {
              addError = err;
            }
            /* Reconcile before any retry: a lost response may follow a committed
               add. The durable stash remains until this read proves the target
               quantities are present. */
            current = await getCart();
            if (
              quantityShape(current.items) !== expectedShape ||
              Number(current.item_count) !== expectedCount ||
              !wantedLineQuantitiesMatch(stash.items, current.items)
            ) {
              throw addError || new Error('restore verification failed');
            }
          } else if (!wantedLineQuantitiesMatch(stash.items, current.items)) {
            throw new Error('restore found an excess stashed-line quantity');
          }
          /* Data commit comes before presentation. A later repaint failure must
             not make this already-restored add eligible for a second retry. */
          clearStash();
          dataRestored = true;
        } catch (err) {
          console.error('[QvaCartSelect] restore', err);
          stash.r = (stash.r || 0) + 1;
          saveStash(stash);
        }
        if (dataRestored) {
          try {
            await repaintAll();
            syncFromDom();
          } catch (err) {
            console.error('[QvaCartSelect] restore repaint', err);
            showError('Giỏ hàng đã được khôi phục. Vui lòng tải lại trang để cập nhật hiển thị.', document.body);
          }
        }
      });
    } finally {
      restoreBusy = false;
      finishCartMutation(cartMutationToken, 'cart-select-restore');
      window.setTimeout(resumeDeferredSelection, 0);
    }
  }

  /* ── delegated listeners (capture; survive Dawn re-renders) ──── */
  document.addEventListener(
    'change',
    (e) => {
      const t = e.target;
      if (!t || t.type !== 'checkbox' || !t.closest) return;
      const lineBox = t.closest('.qva-sel');
      const allBar = lineBox ? null : t.closest('.qva-sel-all');
      if (!lineBox && !allBar) return;
      /* keep this event away from Dawn's CartItems change handler */
      e.stopPropagation();
      if (externalCartMutationIsBusy()) {
        if (lineBox && lineBox.dataset.key) {
          desired.set(lineBox.dataset.key, t.checked);
          t.checked = lineBox.dataset.off === '0';
        } else if (allBar) {
          const want = t.checked;
          document.querySelectorAll('.qva-sel').forEach((box) => {
            if (box.dataset.key) desired.set(box.dataset.key, want);
          });
          const selected = Number(allBar.dataset.selected || 0);
          const total = Number(allBar.dataset.total || 0);
          t.checked = total > 0 && selected === total;
          t.indeterminate = selected > 0 && selected < total;
        }
        showError('Giỏ hàng đang cập nhật. Lựa chọn sẽ được xử lý sau khi hoàn tất.', lineBox || allBar);
        return;
      }
      if (lineBox && lineBox.dataset.key) {
        applyOptimistic(lineBox.dataset.key, t.checked);
        desired.set(lineBox.dataset.key, t.checked);
        scheduleSync();
      } else if (allBar) {
        const want = t.checked;
        const keys = new Set();
        document.querySelectorAll('.qva-sel').forEach((b) => {
          if (b.dataset.key) keys.add(b.dataset.key);
        });
        keys.forEach((k) => {
          applyOptimistic(k, want);
          desired.set(k, want);
        });
        scheduleSync();
      }
    },
    true
  );

  /* ── image tap = toggle (Shopee behavior; owner request 31 Aug 2026) ──
     Dawn overlays the whole media cell with an invisible product link
     (a.cart-item__link), so with the checkbox on the image a tap beside
     the small box navigated to the product — surprising. When the row has
     a checkbox, a click anywhere on the image/cell toggles selection
     instead; the product NAME link keeps navigating. Rows without a
     checkbox (feature off, nested lines) keep stock navigation. */
  document.addEventListener(
    'click',
    (e) => {
      const t = e.target;
      if (!t || !t.closest) return;
      if (t.closest('.qva-sel')) return; /* the real checkbox handles itself */
      const cell = t.closest('td.cart-item__media');
      if (!cell) return;
      const input = cell.querySelector('.qva-sel input');
      if (!input) return; /* feature off or nested line: navigate as stock */
      e.preventDefault();
      e.stopPropagation();
      const boxEl = input.closest('.qva-sel');
      if (!boxEl || !boxEl.dataset.key) return;
      const key = boxEl.dataset.key;
      const cur = desired.has(key) ? desired.get(key) : boxEl.dataset.off === '0';
      if (externalCartMutationIsBusy()) {
        desired.set(key, !cur);
        input.checked = boxEl.dataset.off === '0';
        showError('Giỏ hàng đang cập nhật. Lựa chọn sẽ được xử lý sau khi hoàn tất.', boxEl);
        return;
      }
      applyOptimistic(key, !cur);
      desired.set(key, !cur);
      scheduleSync();
    },
    true
  );

  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target;
      if (!form || (form.id !== 'cart' && form.id !== 'CartDrawer-Form')) return;
      if (form.dataset.qvaBypass === '1') {
        delete form.dataset.qvaBypass;
        return; /* our own re-submit: let it through untouched */
      }
      if (!document.querySelector('.qva-sel')) return; /* feature off / no boxes */
      const gatewayContinuationToken = claimGatewayContinuation(form);
      /* Every checkout is coordinated, even when this tab's DOM looks clean.
         Another tab may be between marker add/remove and temporarily doubled. */
      e.preventDefault();
      e.stopPropagation();
      const submitter =
        e.submitter ||
        document.getElementById(form.id === 'CartDrawer-Form' ? 'CartDrawer-Checkout' : 'checkout');
      /* v1.2: never checkout over an unflushed queue — settle first, then the
         fail-closed path re-reads the server cart as its own source of truth. */
      syncSettled()
        .then(() => partialCheckout(form, submitter, gatewayContinuationToken))
        .catch(() => {
          cancelGatewayContinuation(form, gatewayContinuationToken);
          showError('Giỏ hàng chưa cập nhật xong. Vui lòng thử thanh toán lại.', form);
        });
    },
    true
  );

  const boot = () => {
    syncFromDom();
    window.setTimeout(restoreStash, 0);
    /* v1.2: prime the snapshot so the first optimistic total is instant too
       (v1.3: this read is also what sets S.hasMarkers for the checkout path) */
    getCart()
      .then(syncFromDom)
      .catch(() => { /* non-fatal; checkout's own guarded read will retry */ });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
